import React, { useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';
import Layout from '../../elements/Layout';
import TaskForm from './TaskForm';
import Modal from '../../elements/Modal';
import { fetchSubtasks, fetchTasks } from '../../utils/api/api';
import toast from 'react-hot-toast';
import SubtaskForm from './SubtaskForm';
import Loading from '../../elements/Loading';
import { AuthContext } from '../../utils/context/AuthContext';
import { canAccess } from '../../utils/canAccess';

// Lucide Icons
import {
    Search,
    Plus,
    ChevronDown,
    ChevronUp,
    Clock,
    CheckCircle2,
    AlertCircle,
    ArrowUpCircle,
    CalendarClock,
    User
} from 'lucide-react';

const TaskPriority = ({ priority }) => {
    const getPriorityColor = () => {
        switch (priority) {
            case 'hot':
                return 'bg-red-50 text-red-500 border-red-200';
            case 'warm':
                return 'bg-orange-50 text-orange-500 border-orange-200';
            default:
                return 'bg-gray-50 text-gray-500 border-gray-200';
        }
    };

    const getPriorityIcon = () => {
        switch (priority) {
            case 'hot':
                return <ArrowUpCircle size={14} className="mr-1" />;
            case 'warm':
                return <AlertCircle size={14} className="mr-1" />;
            default:
                return <Clock size={14} className="mr-1" />;
        }
    };

    return (
        <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${getPriorityColor()}`}>
            {getPriorityIcon()}
            {priority}
        </span>
    );
};

const TaskStatus = ({ status }) => {
    const getStatusColor = () => {
        switch (status) {
            case 'completed':
                return 'bg-green-50 text-green-600 border-green-200';
            case 'in_progress':
                return 'bg-blue-50 text-secondary border-blue-200';
            default:
                return 'bg-red-50 text-red-600 border-red-200';
        }
    };

    const getStatusIcon = () => {
        switch (status) {
            case 'completed':
                return <CheckCircle2 size={14} className="mr-1" />;
            case 'in_progress':
                return <Clock size={14} className="mr-1" />;
            default:
                return <AlertCircle size={14} className="mr-1" />;
        }
    };

    return (
        <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor()}`}>
            {getStatusIcon()}
            {status.replace('_', ' ')}
        </span>
    );
};

const TaskCard = ({ task, isOpen, onToggle, onAddSubtask, subtasks }) => {
    return (
        <motion.div
            className="mb-3 overflow-hidden border border-gray-200 rounded-xl bg-white shadow-sm"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <div
                className="p-4 flex flex-col md:flex-row w-full cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={onToggle}
            >
                <div className="flex-grow space-y-1 md:space-y-0.5">
                    <h3 className="font-medium text-gray-900">{task.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{task.description}</p>

                    <div className="flex flex-wrap items-center gap-2 pt-2">
                        <div className="flex items-center text-xs text-gray-500">
                            <CalendarClock size={14} className="mr-1" />
                            {moment(task.due_date).format('MMM D, YYYY')}
                        </div>

                        <div className="flex items-center text-xs text-gray-500">
                            <User size={14} className="mr-1" />
                            {task?.assigned_user?.first_name} {task?.assigned_user?.last_name}
                        </div>
                    </div>
                </div>

                <div className="flex items-center mt-3 md:mt-0 md:ml-4 gap-2">
                    <TaskStatus status={task.status} />
                    <TaskPriority priority={task.priority} />
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onAddSubtask(task.id);
                        }}
                        className="ml-2 flex items-center justify-center text-secondary hover:text-blue-800 text-sm font-medium bg-blue-50 py-1 px-3 rounded-full"
                    >
                        <Plus size={14} className="mr-1" />
                        <span className="hidden sm:inline">Subtask</span>
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="bg-gray-50 border-t border-gray-200"
                    >
                        <div className="p-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                <CheckCircle2 size={16} className="mr-2 text-secondary" />
                                Subtasks
                            </h4>

                            {subtasks?.length > 0 ? (
                                <div className="space-y-2">
                                    {subtasks.map((subtask) => (
                                        <div
                                            key={subtask.id}
                                            className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm"
                                        >
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h5 className="font-medium text-gray-800 text-sm">{subtask.title}</h5>
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        Due: {moment(subtask.due_date).format('MMM D, YYYY')}
                                                    </div>
                                                </div>
                                                <TaskStatus status={subtask.status} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-6 bg-white rounded-lg border border-gray-200">
                                    <p className="text-gray-500 text-sm">No subtasks available.</p>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAddSubtask(task.id);
                                        }}
                                        className="mt-2 text-secondary text-sm font-medium hover:text-blue-800"
                                    >
                                        Add your first subtask
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const Tasks = () => {
    const { user } = useContext(AuthContext);
    const [tasks, setTasks] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [openSubtaskId, setOpenSubtaskId] = useState(null);
    const [subtaskModal, setSubtaskModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [subtasks, setSubtasks] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setLoading(true);
        const getTasks = async () => {
            try {
                const response = await fetchTasks();
                setTasks(response.tasks);
            } catch (error) {
                console.error('Error fetching tasks:', error);
                toast.error('Failed to load tasks');
            } finally {
                setLoading(false);
            }
        };

        getTasks();
    }, []);

    const handleAddTask = (taskData) => {
        setTasks((prevTasks) => [...prevTasks, taskData]);
        setShowModal(false);
        toast.success('Task added successfully');
    };

    const handleSubtaskAdded = (taskId, newSubtask) => {
        setSubtasks((prevSubtasks) => ({
            ...prevSubtasks,
            [taskId]: [...(prevSubtasks[taskId] || []), newSubtask],
        }));
        setSubtaskModal(false);
        toast.success('Subtask added successfully');
    };

    const handleToggleSubtasks = async (taskId) => {
        if (openSubtaskId === taskId) {
            setOpenSubtaskId(null);
            return;
        }

        setOpenSubtaskId(taskId);

        if (!subtasks[taskId]) {
            try {
                setLoading(true);
                const response = await fetchSubtasks({ id: taskId });
                const fetchedSubtasks = response.task.sub_tasks;

                setSubtasks((prevSubtasks) => ({
                    ...prevSubtasks,
                    [taskId]: fetchedSubtasks,
                }));
            } catch (error) {
                toast.error(error.message || 'Failed to fetch subtasks');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleAddSubtask = (taskId) => {
        setSelectedTask(taskId);
        setSubtaskModal(true);
    };

    const filteredTasks = tasks.filter(task =>
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout text="Tasks" backgroundColor="bg-gray-50">
            {loading && <Loading />}

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div className="relative flex-grow max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search size={18} className="text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search tasks..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 w-full bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-secondary focus:border-transparent shadow-sm transition-colors"
                    />
                </div>

                {canAccess(user, 'create_tasks') && (
                    <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setShowModal(true)}
                        className="flex items-center justify-center gap-2 px-5 py-2 bg-secondary hover:bg-secondary text-black font-medium rounded-xl shadow-sm transition-colors whitespace-nowrap"
                    >
                        <Plus size={18} />
                        <span>New Task</span>
                    </motion.button>
                )}
            </div>

            <div className="space-y-1">
                <AnimatePresence>
                    {filteredTasks.length > 0 ? (
                        filteredTasks.map((task) => (
                            <TaskCard
                                key={task.id}
                                task={task}
                                isOpen={openSubtaskId === task.id}
                                onToggle={() => handleToggleSubtasks(task.id)}
                                onAddSubtask={handleAddSubtask}
                                subtasks={subtasks[task.id]}
                            />
                        ))
                    ) : (
                        <motion.div
                            className="bg-white rounded-xl border border-gray-200 p-8 text-center"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            {searchTerm ? (
                                <>
                                    <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-gray-100 mb-4">
                                        <Search className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">No matching tasks</h3>
                                    <p className="text-gray-500 mb-4">Try adjusting your search terms</p>
                                </>
                            ) : (
                                <>
                                    <div className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-blue-50 mb-4">
                                        <CheckCircle2 className="h-8 w-8 text-secondary" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-1">No tasks yet</h3>
                                    <p className="text-gray-500 mb-4">Create your first task to get started</p>
                                    {canAccess(user, 'create_tasks') && (
                                        <motion.button
                                            whileHover={{ scale: 1.03 }}
                                            whileTap={{ scale: 0.97 }}
                                            onClick={() => setShowModal(true)}
                                            className="inline-flex items-center justify-center gap-2 px-5 py-2 bg-secondary hover:bg-secondary text-white font-medium rounded-lg shadow-sm transition-colors"
                                        >
                                            <Plus size={16} />
                                            <span>New Task</span>
                                        </motion.button>
                                    )}
                                </>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {showModal && (
                <Modal onClose={() => setShowModal(false)} title="Create New Task">
                    <TaskForm onAddTask={handleAddTask} />
                </Modal>
            )}

            {subtaskModal && (
                <Modal onClose={() => setSubtaskModal(false)} title="Add Subtask">
                    <SubtaskForm
                        taskId={selectedTask}
                        onSubtaskAdded={handleSubtaskAdded}
                    />
                </Modal>
            )}
        </Layout>
    );
};

export default Tasks;